export const BF_COLOURS = ["HOT", "COLD", "RED", "MEASURE"] as const;
// "verify" is a placeholder for the future tote stock-check feature: rendered
// greyed out, never enabled.
export const BF_STATUS_TOTE_ACTIONS = ["swap", "edit", "verify"] as const;

export type BfColour = typeof BF_COLOURS[number];

// Sheet variant, stamped on the session by the BE from the desk's bfMode.
//   standard — HOT and COLD run paired totes at all times, RED and MEASURE a
//              single tote (six totes).
//   big      — one HOT tote (hot and cold combined: every big item goes low),
//              one RED, one MEASURE, plus the BE's over-length RED rule and
//              the length pop-up for measured-but-dimensionless SKUs.
//   sourcing — Goods In › Sourcing: one HOT, one COLD, one MEASURE, scanning
//              against a Flow Sourcing PO (no RED: being on the PO is the
//              acceptance).
export type BfMode = "standard" | "big" | "sourcing";

export const BF_MODE_CONFIG: Record<BfMode, {
    colours: readonly BfColour[];
    required: Partial<Record<BfColour, number>>;
}> = {
    standard: {
        colours: ["HOT", "COLD", "RED", "MEASURE"],
        required: { HOT: 2, COLD: 2, RED: 1, MEASURE: 1 },
    },
    big: {
        colours: ["HOT", "RED", "MEASURE"],
        required: { HOT: 1, RED: 1, MEASURE: 1 },
    },
    sourcing: {
        colours: ["HOT", "COLD", "MEASURE"],
        required: { HOT: 1, COLD: 1, MEASURE: 1 },
    },
};

export const normaliseBfMode = (value: unknown): BfMode =>
    value === "big" || value === "sourcing" ? value : "standard";

// Sourcing check refusals (BE sourcingScan.js) → message key. Only these can
// be overridden by a team leader's Accept anyway.
export const SOURCING_OVERRIDABLE = ["not_on_po", "over_ordered"] as const;
export const sourcingRefusalKey = (reason?: string): string | null => {
    const known: Record<string, string> = {
        not_on_po: "sourcing_error_not_on_po",
        over_ordered: "sourcing_error_over_ordered",
        unknown_item: "sourcing_error_unknown_item",
        product_missing: "sourcing_error_product_missing",
        no_sourcing_po: "sourcing_error_no_po",
        sourcing_po_changed: "sourcing_error_po_changed",
        override_not_allowed: "sourcing_error_override_not_allowed",
    };
    return known[reason ?? ""] ?? null;
};

// The colours a session runs, in setup / finish order.
export const coloursFor = (mode: BfMode): readonly BfColour[] => BF_MODE_CONFIG[mode].colours;

// Required open totes per colour == the colour's max. Item scanning is gated
// (setup phase) until every mode colour holds its full complement. A colour
// outside the mode requires zero and never appears on the sheet.
export const requiredFor = (mode: BfMode, colour: BfColour): number =>
    BF_MODE_CONFIG[mode].required[colour] ?? 0;

// The classic six-tote rule table, kept for callers that predate modes.
export const BF_COLOUR_REQUIRED: Record<BfColour, number> = { HOT: 2, COLD: 2, RED: 1, MEASURE: 1 };
export type BfVerdict = BfColour | "BLACK" | "ERROR";
export type BfPanel = "GREEN-HOT" | "GREEN-COLD" | "RED" | "PURPLE" | "BLACK" | "NOT-ON-PORTAL" | "ERROR" | "BIG";

export interface BfTote {
    id?: number | string;
    colour: BfColour;
    toteId: string;
    openedAt?: string;
    units?: number;
    // Sum of the confirmed units' Portal product weights (BE-computed).
    weightGrams?: number;
}

// Colours the standard sheet weight-limits (Alex 2026-09-18). Big mode is
// never limited: the BE sends a null limit for big sessions.
export const BF_WEIGHT_LIMITED_COLOURS: readonly BfColour[] = ["HOT", "COLD"];

export interface BfCheckResult {
    ok: boolean;
    verdict: BfVerdict;
    reason: string;
    fnsku: string;
    // The manifest's real X00… FNSKU when the scan resolved via a manifest.
    // `fnsku` above is the scanned code, which may be an EAN, SKU or ASIN.
    manifestFnsku?: string | null;
    asin?: string | null;
    sku?: string | null;
    title?: string | null;
    imageUrl?: string | null;
    hasDims?: boolean;
    eventId: number | string;
    expectedToteId?: string | null;
    expectedToteIds?: string[];
    hotTarget?: number | null;
    hotCurrent?: number | null;
    hotRemaining?: number | null;
    hotBreakdown?: unknown;
    unitsSold?: number | null;
    windowDays?: number | null;
    coverDays?: number | null;
    // Big mode: the computed hot/cold behind a HOT verdict, the over-length
    // rule inputs, and whether the length pop-up must be answered first.
    hotCold?: "HOT" | "COLD" | null;
    longestCm?: number | null;
    maxLengthCm?: number | null;
    lengthUnknown?: boolean;
    lengthAnswer?: "over" | "under" | null;
    // New-SKU seeding: a HOT verdict for a SKU with no BF sales data.
    newSku?: boolean;
}

export interface BfPreviousTote {
    toteId: string;
    booked: boolean;
    units: number;
}

export interface BfCloseResult extends BfPreviousTote {
    skus?: number;
}

export type BfBookedTotals = Record<BfColour, number>;

export interface BfFinishSummary {
    session: unknown;
    bookedTotals: BfBookedTotals;
}

export interface BfEditScope {
    colour: BfColour;
    toteId: string | null;
}

export type BfReopenMode = "carry-on" | "start-over";

export interface BfReopenPrompt {
    toteId: string;
    colour: BfColour;
    units: number;
    closedAt: string | null;
    closedBy: string;
}

export interface BfItemClosePrompt {
    toteId: string;
    colour: BfColour;
    // "weight": the tote reached the weight limit — the close cannot be
    // cancelled and nothing else scans until this tote is closed.
    reason?: "weight";
}

export const isForcedItemClose = (prompt: BfItemClosePrompt | null | undefined): boolean =>
    prompt?.reason === "weight";

export type BfPhase =
    | "loading"
    | "setup"
    | "item"
    | "confirm"
    | "edit"
    | "item-close"
    | "reopen"
    | "reopen-confirming"
    | "swap"
    | "finish-pending"
    | "finish-sequence"
    | "finished";

export interface BfFlowState {
    phase: BfPhase;
    mode: BfMode;
    backNavigationLocked: boolean;
    pending: BfCheckResult | null;
    // Big mode: the pending HOT unit needs the operator's over-length answer
    // before it can be confirmed; scans are ignored while this is up.
    lengthPrompt: boolean;
    bigFeedback: boolean;
    editScope: BfEditScope | null;
    itemClosePrompt: BfItemClosePrompt | null;
    reopenPrompt: BfReopenPrompt | null;
    reopenStartOver: boolean;
    swapColour: BfColour | null;
    finishColour: BfColour | null;
    closedColours: BfColour[];
    finishedSummary: BfFinishSummary | null;
    lastEditOutcome: { colour: BfColour; toteId: string } | null;
    toteUnits: Record<string, number>;
    // Per-tote confirmed weight (grams) and the limit that applies to HOT /
    // COLD totes in this session (null = no limit, e.g. big mode).
    toteWeights: Record<string, number>;
    weightLimitGrams: number | null;
    lastToteOutcome:
        | { kind: "close"; tote: BfCloseResult }
        | { kind: "swap"; tote: BfPreviousTote }
        | null;
}

export type BfFlowAction =
    | { type: "SESSION_READY"; openTotes: BfTote[]; pending?: BfCheckResult | null; mode?: BfMode; weightLimitGrams?: number | null }
    | { type: "TOTE_OPENED"; openTotes: BfTote[]; colour: BfColour }
    | { type: "CHECKED"; result: BfCheckResult }
    | { type: "LENGTH_ANSWERED"; result: BfCheckResult }
    | { type: "REOPEN_LENGTH_PROMPT" }
    | { type: "CLEAR_RESULT" }
    | { type: "CONFIRMED" }
    | { type: "BIG_CANCELLED" }
    | { type: "BIG_CANCEL_REJECTED" }
    | { type: "CLEAR_BIG_FEEDBACK" }
    | { type: "TOTE_UNIT_CONFIRMED"; toteId: string; weightGrams?: number | null }
    | { type: "START_EDIT"; colour: BfColour; openTotes: BfTote[] }
    | { type: "SELECT_EDIT_TOTE"; toteId: string }
    | { type: "EXIT_EDIT"; openTotes: BfTote[] }
    | { type: "EDIT_SUCCEEDED"; toteId: string; weightGrams?: number | null }
    | { type: "EDIT_REJECTED" }
    | { type: "CLEAR_EDIT_OUTCOME" }
    | { type: "RECLASSIFIED"; result: BfCheckResult }
    | { type: "STAGE_ITEM_TOTE_CLOSE"; tote: BfTote; reason?: "weight" }
    | { type: "CANCEL_ITEM_TOTE_CLOSE"; openTotes: BfTote[] }
    | { type: "ITEM_TOTE_CLOSED"; colour: BfColour; openTotes: BfTote[]; result: BfCloseResult }
    | { type: "OFFER_TOTE_REOPEN"; prompt: BfReopenPrompt }
    | { type: "STAGE_REOPEN_START_OVER" }
    | { type: "UNSTAGE_REOPEN_START_OVER" }
    | { type: "CONFIRM_TOTE_REOPEN" }
    | { type: "CANCEL_TOTE_REOPEN" }
    | { type: "TOTE_REOPEN_FAILED" }
    | { type: "TOTE_REOPENED"; tote: BfTote; openTotes: BfTote[] }
    | { type: "START_SWAP"; colour: BfColour; openTotes: BfTote[] }
    | { type: "TOTE_SWAPPED"; previousTote: BfPreviousTote; openTotes: BfTote[] }
    | { type: "TOTE_CLOSED_BY_SWAP"; colour: BfColour; openTotes: BfTote[]; result: BfCloseResult }
    | { type: "STOP_SWAP"; openTotes: BfTote[] }
    | { type: "START_FINISH"; openTotes: BfTote[] }
    | { type: "ABANDON_PENDING_AND_START_FINISH"; openTotes: BfTote[] }
    | { type: "CONFIRMED_AND_START_FINISH"; openTotes: BfTote[] }
    | { type: "FINISH_CLOSE_REJECTED" }
    | { type: "CANCEL_FINISH"; openTotes: BfTote[] }
    | { type: "TOTE_CLOSED"; colour: BfColour; openTotes: BfTote[]; result: BfCloseResult }
    | { type: "FINISHED"; summary: BfFinishSummary }
    | { type: "CLEAR_TOTE_OUTCOME" };

export const INITIAL_BF_FLOW: BfFlowState = {
    phase: "loading",
    mode: "standard",
    backNavigationLocked: false,
    pending: null,
    lengthPrompt: false,
    bigFeedback: false,
    editScope: null,
    itemClosePrompt: null,
    reopenPrompt: null,
    reopenStartOver: false,
    swapColour: null,
    finishColour: null,
    closedColours: [],
    finishedSummary: null,
    lastEditOutcome: null,
    toteUnits: {},
    toteWeights: {},
    weightLimitGrams: null,
    lastToteOutcome: null,
};

const hasToteVerdict = (pending: BfCheckResult | null): pending is BfCheckResult =>
    !!pending && BF_COLOURS.includes(pending.verdict as BfColour);

const scanLoopPhase = (pending: BfCheckResult | null): BfPhase => hasToteVerdict(pending) ? "confirm" : "item";

export const verdictToPanel = (verdict: BfVerdict): BfPanel => {
    if (verdict === "HOT") return "GREEN-HOT";
    if (verdict === "COLD") return "GREEN-COLD";
    if (verdict === "MEASURE") return "PURPLE";
    return verdict;
};

// Manifested but missing from Portal products — a catalogue gap, not a scan
// failure, so it gets a held panel (like BLACK) instead of the error flash.
export const isNotOnPortal = (result: Pick<BfCheckResult, "verdict" | "reason">): boolean =>
    result.verdict === "ERROR" && result.reason === "not_on_portal";

export const resultToPanel = (result: BfCheckResult): BfPanel =>
    isNotOnPortal(result) ? "NOT-ON-PORTAL" : verdictToPanel(result.verdict);

// First mode colour holding fewer open totes than its required complement.
// Non-null drives the setup phase, so this is the item-scanning gate: a
// HOT/COLD pair losing one tote forces its replacement before scans resume.
export const nextMissingColour = (openTotes: BfTote[], mode: BfMode = "standard"): BfColour | null =>
    coloursFor(mode).find(colour =>
        openTotes.filter(tote => tote.colour === colour).length < requiredFor(mode, colour)) ?? null;

// Big mode: a pending HOT unit whose SKU has no dimensions on file and no
// remembered answer — the operator must say whether it is over the max
// length before the tote scan.
export const needsLengthAnswer = (result: BfCheckResult | null): boolean =>
    !!result && result.verdict === "HOT" && result.lengthUnknown === true;

// A pending unit classified from a remembered pop-up answer (this or an
// earlier operator's). The sheet offers "Change answer" on these so a wrong
// tap can be fixed on the spot; once dims exist the reason is never these.
export const hasRememberedLengthAnswer = (result: BfCheckResult | null): boolean =>
    !!result && (result.reason === "operator_over_length" || result.reason === "operator_under_length");

export const toteForColour = (openTotes: BfTote[], colour: BfColour): BfTote | undefined =>
    openTotes.find(tote => tote.colour === colour);

export const totesForColour = (openTotes: BfTote[], colour: BfColour): BfTote[] =>
    openTotes.filter(tote => tote.colour === colour);

export const toteIdsForColour = (openTotes: BfTote[], colour: BfColour): string[] =>
    totesForColour(openTotes, colour).map(tote => tote.toteId);

export const nextFinishColour = (openTotes: BfTote[], mode: BfMode = "standard"): BfColour | null => {
    const open = new Set(openTotes.map(tote => tote.colour));
    // Mode colours first in order; any stray colour (e.g. a pre-mode tote)
    // still gets closed rather than blocking finish forever.
    return coloursFor(mode).find(colour => open.has(colour))
        ?? BF_COLOURS.find(colour => open.has(colour))
        ?? null;
};

export const normaliseBfBarcode = (value: string): string => value.trim().toUpperCase();

const toteUnitsFromState = (
    openTotes: BfTote[],
    current: Record<string, number> = {},
): Record<string, number> => openTotes.reduce<Record<string, number>>((units, tote) => {
    const toteKey = normaliseBfBarcode(tote.toteId);
    units[toteKey] = Object.prototype.hasOwnProperty.call(current, toteKey)
        ? current[toteKey]
        : Math.max(0, Number(tote.units) || 0);
    return units;
}, {});

const toteWeightsFromState = (
    openTotes: BfTote[],
    current: Record<string, number> = {},
): Record<string, number> => openTotes.reduce<Record<string, number>>((weights, tote) => {
    const toteKey = normaliseBfBarcode(tote.toteId);
    weights[toteKey] = Object.prototype.hasOwnProperty.call(current, toteKey)
        ? current[toteKey]
        : Math.max(0, Number(tote.weightGrams) || 0);
    return weights;
}, {});

// A server-reported tote weight replaces the local figure; an absent one
// (older BE, failed parse) leaves it alone.
const setToteWeight = (
    state: BfFlowState,
    toteId: string,
    weightGrams: number | null | undefined,
): Record<string, number> => {
    const toteKey = normaliseBfBarcode(toteId);
    const weight = Number(weightGrams);
    if (weightGrams == null || !Number.isFinite(weight)) return state.toteWeights;
    return { ...state.toteWeights, [toteKey]: Math.max(0, weight) };
};

export const toteWeightOf = (state: Pick<BfFlowState, "toteWeights">, tote: BfTote): number =>
    state.toteWeights[normaliseBfBarcode(tote.toteId)] ?? Math.max(0, Number(tote.weightGrams) || 0);

export const isWeightLimitedColour = (
    state: Pick<BfFlowState, "weightLimitGrams">,
    colour: BfColour,
): boolean => state.weightLimitGrams != null && state.weightLimitGrams > 0
    && BF_WEIGHT_LIMITED_COLOURS.includes(colour);

export const isToteAtWeightLimit = (
    state: Pick<BfFlowState, "toteWeights" | "weightLimitGrams">,
    tote: BfTote,
): boolean => isWeightLimitedColour(state, tote.colour)
    && toteWeightOf(state, tote) >= (state.weightLimitGrams as number);

// The first open tote that has reached the weight limit: the sheet forces it
// closed (sent to put-away) before any other scan. Derived from state, so a
// resumed session with a heavy tote is forced too.
export const findOverweightTote = (
    openTotes: BfTote[],
    state: Pick<BfFlowState, "toteWeights" | "weightLimitGrams">,
): BfTote | undefined => openTotes.find(tote => isToteAtWeightLimit(state, tote));

const adjustToteUnits = (state: BfFlowState, toteId: string, delta: number): Record<string, number> => {
    const toteKey = normaliseBfBarcode(toteId);
    if (!Object.prototype.hasOwnProperty.call(state.toteUnits, toteKey)) return state.toteUnits;
    return {
        ...state.toteUnits,
        [toteKey]: Math.max(0, state.toteUnits[toteKey] + delta),
    };
};

export const findColourTote = (openTotes: BfTote[], colour: BfColour, scanned: string): BfTote | undefined => {
    const value = normaliseBfBarcode(scanned);
    return totesForColour(openTotes, colour)
        .find(tote => normaliseBfBarcode(tote.toteId) === value);
};

export const findOpenTote = (openTotes: BfTote[], scanned: string): BfTote | undefined => {
    const value = normaliseBfBarcode(scanned);
    return openTotes.find(tote => normaliseBfBarcode(tote.toteId) === value);
};

export type BfItemScanRoute =
    | { kind: "stage-close"; tote: BfTote }
    | { kind: "confirm-close"; tote: BfTote }
    | { kind: "weight-blocked"; tote: BfTote }
    | { kind: "check-item" };

export const routeBfItemScan = (
    state: Pick<BfFlowState, "phase" | "pending" | "bigFeedback" | "itemClosePrompt">,
    openTotes: BfTote[],
    scanned: string,
): BfItemScanRoute | null => {
    if (state.phase === "item-close" && state.itemClosePrompt) {
        const tote = findOpenTote(openTotes, scanned);
        const isPromptTote = !!tote
            && normaliseBfBarcode(tote.toteId) === normaliseBfBarcode(state.itemClosePrompt.toteId);
        if (isForcedItemClose(state.itemClosePrompt)) {
            // Weight-limit close: only the full tote's own barcode does
            // anything; items and other totes are refused until it closes.
            const forcedTote = findOpenTote(openTotes, state.itemClosePrompt.toteId) ?? tote;
            if (isPromptTote && tote) return { kind: "confirm-close", tote };
            return { kind: "weight-blocked", tote: forcedTote ?? { toteId: state.itemClosePrompt.toteId, colour: state.itemClosePrompt.colour } };
        }
        if (!tote) return { kind: "check-item" };
        return isPromptTote
            ? { kind: "confirm-close", tote }
            : { kind: "stage-close", tote };
    }
    if (state.phase !== "item" || state.pending || state.bigFeedback) return null;
    const tote = findOpenTote(openTotes, scanned);
    return tote ? { kind: "stage-close", tote } : { kind: "check-item" };
};

export const findExpectedTote = (
    result: BfCheckResult,
    openTotes: BfTote[],
    scanned: string,
): BfTote | undefined => {
    if (!BF_COLOURS.includes(result.verdict as BfColour)) return undefined;
    const expected = result.expectedToteIds?.length
        ? result.expectedToteIds
        : result.expectedToteId
            ? [result.expectedToteId]
            : toteIdsForColour(openTotes, result.verdict as BfColour);
    const value = normaliseBfBarcode(scanned);
    if (!expected.some(toteId => normaliseBfBarcode(toteId) === value)) return undefined;
    return totesForColour(openTotes, result.verdict as BfColour)
        .find(tote => normaliseBfBarcode(tote.toteId) === value);
};

export const isExpectedTote = (result: BfCheckResult, openTotes: BfTote[], scanned: string): boolean =>
    !!findExpectedTote(result, openTotes, scanned);

export type BfConfirmScanRoute = "tote" | "big" | "error";

export const routeBfConfirmScan = (
    result: BfCheckResult,
    openTotes: BfTote[],
    scanned: string,
): BfConfirmScanRoute => {
    if (isExpectedTote(result, openTotes, scanned)) return "tote";
    if (normaliseBfBarcode(scanned) === "BIG") return "big";
    return "error";
};

export const buildBfUncommitPayload = (fnsku: string, editScope: BfEditScope) => ({
    fnsku,
    toteId: editScope.toteId,
});

export const pendingFromEvent = (event: any, openTotes: BfTote[]): BfCheckResult | null => {
    const verdict = event?.verdict ?? event?.colour;
    const eventId = event?.event_id ?? event?.eventId ?? event?.id;
    if (!event || !eventId || !BF_COLOURS.includes(verdict as BfColour)) return null;
    if (event.confirmed_at || event.confirmedAt || event.cancelled_at || event.cancelledAt) return null;

    const expectedToteIds = toteIdsForColour(openTotes, verdict as BfColour);
    if (expectedToteIds.length === 0) return null;

    return {
        ...event,
        ok: true,
        verdict,
        eventId,
        fnsku: event.fnsku ?? "",
        reason: event.reason ?? "",
        expectedToteId: expectedToteIds[0],
        expectedToteIds,
    };
};

const refreshPendingTote = (pending: BfCheckResult | null, openTotes: BfTote[]): BfCheckResult | null => {
    if (!pending || !BF_COLOURS.includes(pending.verdict as BfColour)) return pending;
    const expectedToteIds = toteIdsForColour(openTotes, pending.verdict as BfColour);
    return {
        ...pending,
        expectedToteId: expectedToteIds[0] ?? null,
        expectedToteIds,
    };
};

export const bfFlowReducer = (state: BfFlowState, action: BfFlowAction): BfFlowState => {
    switch (action.type) {
        case "SESSION_READY": {
            const mode = action.mode ?? state.mode;
            const backNavigationLocked = action.openTotes.length > 0;
            const toteUnits = toteUnitsFromState(action.openTotes);
            const toteWeights = toteWeightsFromState(action.openTotes);
            const limit = Number(action.weightLimitGrams);
            const weightLimitGrams = action.weightLimitGrams != null && Number.isFinite(limit) && limit > 0 ? limit : null;
            const base = { ...INITIAL_BF_FLOW, mode, backNavigationLocked, toteUnits, toteWeights, weightLimitGrams };
            if (nextMissingColour(action.openTotes, mode)) {
                return { ...base, phase: "setup" };
            }
            if (action.pending) {
                return {
                    ...base,
                    phase: scanLoopPhase(action.pending),
                    pending: action.pending,
                    lengthPrompt: needsLengthAnswer(action.pending),
                };
            }
            return { ...base, phase: "item" };
        }
        case "TOTE_OPENED":
            return {
                ...state,
                phase: nextMissingColour(action.openTotes, state.mode) ? "setup" : scanLoopPhase(state.pending),
                backNavigationLocked: true,
                pending: refreshPendingTote(state.pending, action.openTotes),
                itemClosePrompt: null,
                reopenPrompt: null,
                swapColour: null,
                finishColour: null,
                closedColours: state.closedColours.filter(colour => colour !== action.colour),
                toteUnits: toteUnitsFromState(action.openTotes, state.toteUnits),
                toteWeights: toteWeightsFromState(action.openTotes, state.toteWeights),
                lastToteOutcome: null,
            };
        case "CHECKED":
            return {
                ...state,
                phase: scanLoopPhase(action.result),
                pending: action.result,
                lengthPrompt: needsLengthAnswer(action.result),
                bigFeedback: false,
                editScope: null,
                itemClosePrompt: null,
                swapColour: null,
                finishColour: null,
                lastToteOutcome: null,
                lastEditOutcome: null,
            };
        case "LENGTH_ANSWERED":
            // The answered unit replaces the pending one (same event, possibly
            // now RED) and the confirm-by-tote step resumes.
            if (!state.lengthPrompt || !state.pending) return state;
            return {
                ...state,
                phase: scanLoopPhase(action.result),
                pending: action.result,
                lengthPrompt: needsLengthAnswer(action.result),
            };
        case "REOPEN_LENGTH_PROMPT":
            // "Change answer": re-ask for a pending unit that was classified
            // from a remembered answer, before its tote scan.
            if (state.phase !== "confirm" || !hasRememberedLengthAnswer(state.pending)) return state;
            return { ...state, lengthPrompt: true };
        case "CLEAR_RESULT":
            return {
                ...state,
                phase: state.phase === "item" || state.phase === "confirm" ? "item" : state.phase,
                pending: null,
                lengthPrompt: false,
                bigFeedback: false,
            };
        case "CONFIRMED":
            return {
                ...state,
                phase: "item",
                pending: null,
                lengthPrompt: false,
                bigFeedback: false,
                editScope: null,
                itemClosePrompt: null,
                reopenPrompt: null,
                swapColour: null,
                finishColour: null,
            };
        case "BIG_CANCELLED":
            if (state.phase !== "confirm" || !state.pending) return state;
            return {
                ...state,
                phase: "item",
                pending: null,
                lengthPrompt: false,
                bigFeedback: true,
                editScope: null,
                swapColour: null,
                finishColour: null,
            };
        case "BIG_CANCEL_REJECTED":
            return state;
        case "CLEAR_BIG_FEEDBACK":
            return state.bigFeedback ? { ...state, bigFeedback: false } : state;
        case "TOTE_UNIT_CONFIRMED":
            return {
                ...state,
                toteUnits: adjustToteUnits(state, action.toteId, 1),
                toteWeights: setToteWeight(state, action.toteId, action.weightGrams),
            };
        case "START_EDIT": {
            if (state.phase !== "item" && state.phase !== "edit") return state;
            if (state.phase === "edit" && state.editScope?.colour === action.colour) {
                return { ...state, phase: "item", editScope: null, lastEditOutcome: null };
            }
            const colourTotes = totesForColour(action.openTotes, action.colour);
            if (colourTotes.length === 0) return state;
            return {
                ...state,
                phase: "edit",
                pending: null,
                bigFeedback: false,
                editScope: { colour: action.colour, toteId: colourTotes.length === 1 ? colourTotes[0].toteId : null },
                itemClosePrompt: null,
                swapColour: null,
                finishColour: null,
                lastEditOutcome: null,
            };
        }
        case "SELECT_EDIT_TOTE":
            if (state.phase !== "edit" || !state.editScope) return state;
            return { ...state, editScope: { ...state.editScope, toteId: action.toteId } };
        case "EXIT_EDIT":
            return {
                ...state,
                phase: nextMissingColour(action.openTotes, state.mode) ? "setup" : "item",
                editScope: null,
                lastEditOutcome: null,
            };
        case "EDIT_SUCCEEDED":
            if (!state.editScope?.toteId) return state;
            return {
                ...state,
                phase: "item",
                editScope: null,
                lastEditOutcome: { colour: state.editScope.colour, toteId: action.toteId },
                toteUnits: adjustToteUnits(state, action.toteId, -1),
                toteWeights: setToteWeight(state, action.toteId, action.weightGrams),
            };
        case "EDIT_REJECTED":
            return state;
        case "CLEAR_EDIT_OUTCOME":
            return { ...state, lastEditOutcome: null };
        case "RECLASSIFIED":
            return {
                ...state,
                phase: scanLoopPhase(action.result),
                pending: action.result,
                lengthPrompt: needsLengthAnswer(action.result),
                editScope: null,
                itemClosePrompt: null,
                swapColour: null,
                finishColour: null,
            };
        case "STAGE_ITEM_TOTE_CLOSE":
            // Staging from setup is the paired close: after one of a HOT/COLD
            // pair closes, its still-open sibling may be closed too before
            // both replacements are opened.
            if (state.phase !== "item" && state.phase !== "item-close" && state.phase !== "setup") return state;
            if (state.phase === "item" && (state.pending || state.bigFeedback)) return state;
            // A weight-limit close owns the prompt: another tote cannot take
            // it over until the full tote is closed.
            if (state.phase === "item-close" && isForcedItemClose(state.itemClosePrompt)
                && normaliseBfBarcode(state.itemClosePrompt?.toteId ?? "") !== normaliseBfBarcode(action.tote.toteId)) {
                return state;
            }
            return {
                ...state,
                phase: "item-close",
                pending: null,
                bigFeedback: false,
                editScope: null,
                itemClosePrompt: {
                    toteId: action.tote.toteId,
                    colour: action.tote.colour,
                    ...(action.reason ? { reason: action.reason } : {}),
                },
                reopenPrompt: null,
                swapColour: null,
                finishColour: null,
                lastToteOutcome: null,
                lastEditOutcome: null,
            };
        case "CANCEL_ITEM_TOTE_CLOSE":
            if (state.phase !== "item-close" || !state.itemClosePrompt) return state;
            // A weight-limit close cannot be cancelled: the tote must go.
            if (isForcedItemClose(state.itemClosePrompt)) return state;
            // A setup-origin prompt (session short a tote) must fall back to
            // setup, not the item phase where /check would be gated.
            return {
                ...state,
                phase: nextMissingColour(action.openTotes, state.mode) ? "setup" : "item",
                itemClosePrompt: null,
            };
        case "ITEM_TOTE_CLOSED":
            if (state.phase !== "item-close" || !state.itemClosePrompt) return state;
            return {
                ...state,
                phase: nextMissingColour(action.openTotes, state.mode) ? "setup" : "item",
                pending: null,
                editScope: null,
                itemClosePrompt: null,
                reopenPrompt: null,
                swapColour: null,
                finishColour: null,
                closedColours: totesForColour(action.openTotes, action.colour).length === 0
                    ? Array.from(new Set([...state.closedColours, action.colour]))
                    : state.closedColours,
                toteUnits: toteUnitsFromState(action.openTotes, state.toteUnits),
                toteWeights: toteWeightsFromState(action.openTotes, state.toteWeights),
                lastToteOutcome: { kind: "close", tote: action.result },
            };
        case "OFFER_TOTE_REOPEN":
            if (state.phase !== "setup") return state;
            return {
                ...state,
                phase: "reopen",
                itemClosePrompt: null,
                reopenPrompt: action.prompt,
                reopenStartOver: false,
                lastToteOutcome: null,
                lastEditOutcome: null,
            };
        case "STAGE_REOPEN_START_OVER":
            if (state.phase !== "reopen" || !state.reopenPrompt) return state;
            return { ...state, reopenStartOver: true };
        case "UNSTAGE_REOPEN_START_OVER":
            if (state.phase !== "reopen" || !state.reopenPrompt) return state;
            return { ...state, reopenStartOver: false };
        case "CONFIRM_TOTE_REOPEN":
            if (state.phase !== "reopen" || !state.reopenPrompt) return state;
            return { ...state, phase: "reopen-confirming" };
        case "CANCEL_TOTE_REOPEN":
            if (state.phase !== "reopen" || !state.reopenPrompt) return state;
            return {
                ...state,
                phase: "setup",
                reopenPrompt: null,
                reopenStartOver: false,
            };
        case "TOTE_REOPEN_FAILED":
            if (state.phase !== "reopen-confirming" || !state.reopenPrompt) return state;
            return {
                ...state,
                phase: "setup",
                reopenPrompt: null,
                reopenStartOver: false,
            };
        case "TOTE_REOPENED": {
            if (state.phase !== "reopen-confirming" || !state.reopenPrompt) return state;
            const toteKey = normaliseBfBarcode(action.tote.toteId);
            const serverUnits = Math.max(0, Number(action.tote.units) || 0);
            return {
                ...state,
                phase: nextMissingColour(action.openTotes, state.mode) ? "setup" : scanLoopPhase(state.pending),
                backNavigationLocked: true,
                pending: refreshPendingTote(state.pending, action.openTotes),
                reopenPrompt: null,
                reopenStartOver: false,
                swapColour: null,
                finishColour: null,
                closedColours: state.closedColours.filter(colour => colour !== action.tote.colour),
                toteUnits: toteUnitsFromState(action.openTotes, {
                    ...state.toteUnits,
                    [toteKey]: serverUnits,
                }),
                toteWeights: toteWeightsFromState(action.openTotes, {
                    ...state.toteWeights,
                    [toteKey]: Math.max(0, Number(action.tote.weightGrams) || 0),
                }),
                lastToteOutcome: null,
            };
        }
        case "START_SWAP":
            if (state.phase !== "item" && state.phase !== "confirm" && state.phase !== "edit") return state;
            return {
                ...state,
                phase: "swap",
                bigFeedback: false,
                editScope: state.phase === "edit" ? state.editScope : null,
                itemClosePrompt: null,
                reopenPrompt: null,
                swapColour: action.colour,
                finishColour: null,
                lastToteOutcome: null,
                lastEditOutcome: null,
            };
        case "TOTE_SWAPPED": {
            const editScopeStillOpen = state.editScope
                && action.openTotes.some(tote => tote.colour === state.editScope?.colour
                    && tote.toteId === state.editScope.toteId);
            return {
                ...state,
                phase: editScopeStillOpen
                    ? "edit"
                    : nextMissingColour(action.openTotes, state.mode) ? "setup" : scanLoopPhase(state.pending),
                pending: refreshPendingTote(state.pending, action.openTotes),
                editScope: editScopeStillOpen ? state.editScope : null,
                swapColour: null,
                toteUnits: toteUnitsFromState(action.openTotes, state.toteUnits),
                toteWeights: toteWeightsFromState(action.openTotes, state.toteWeights),
                lastToteOutcome: { kind: "swap", tote: action.previousTote },
            };
        }
        case "TOTE_CLOSED_BY_SWAP": {
            const editScopeStillOpen = state.editScope
                && action.openTotes.some(tote => tote.colour === state.editScope?.colour
                    && tote.toteId === state.editScope.toteId);
            // Closing one of a required pair via swap forces the replacement
            // scan before the loop resumes, same as an item-phase close.
            return {
                ...state,
                phase: editScopeStillOpen
                    ? "edit"
                    : nextMissingColour(action.openTotes, state.mode) ? "setup" : scanLoopPhase(state.pending),
                pending: refreshPendingTote(state.pending, action.openTotes),
                editScope: editScopeStillOpen ? state.editScope : null,
                swapColour: null,
                toteUnits: toteUnitsFromState(action.openTotes, state.toteUnits),
                toteWeights: toteWeightsFromState(action.openTotes, state.toteWeights),
                lastToteOutcome: { kind: "close", tote: action.result },
            };
        }
        case "STOP_SWAP":
            return {
                ...state,
                phase: state.editScope
                    ? "edit"
                    : nextMissingColour(action.openTotes, state.mode) ? "setup" : scanLoopPhase(state.pending),
                swapColour: null,
            };
        case "START_FINISH": {
            const hasPendingConfirmation = hasToteVerdict(state.pending);
            return {
                ...state,
                phase: hasPendingConfirmation ? "finish-pending" : "finish-sequence",
                bigFeedback: false,
                editScope: null,
                itemClosePrompt: null,
                reopenPrompt: null,
                swapColour: null,
                finishColour: hasPendingConfirmation ? null : nextFinishColour(action.openTotes, state.mode),
                lastToteOutcome: null,
                lastEditOutcome: null,
            };
        }
        case "ABANDON_PENDING_AND_START_FINISH":
            return {
                ...state,
                phase: "finish-sequence",
                pending: null,
                lengthPrompt: false,
                editScope: null,
                itemClosePrompt: null,
                reopenPrompt: null,
                swapColour: null,
                finishColour: nextFinishColour(action.openTotes, state.mode),
            };
        case "CONFIRMED_AND_START_FINISH":
            return {
                ...state,
                phase: "finish-sequence",
                pending: null,
                lengthPrompt: false,
                editScope: null,
                itemClosePrompt: null,
                reopenPrompt: null,
                swapColour: null,
                finishColour: nextFinishColour(action.openTotes, state.mode),
            };
        case "CANCEL_FINISH":
            return {
                ...state,
                phase: nextMissingColour(action.openTotes, state.mode) ? "setup" : scanLoopPhase(state.pending),
                editScope: null,
                finishColour: null,
            };
        case "FINISH_CLOSE_REJECTED":
            return state;
        case "TOTE_CLOSED":
            return {
                ...state,
                phase: "finish-sequence",
                pending: refreshPendingTote(state.pending, action.openTotes),
                editScope: null,
                finishColour: nextFinishColour(action.openTotes, state.mode),
                closedColours: totesForColour(action.openTotes, action.colour).length === 0
                    ? Array.from(new Set([...state.closedColours, action.colour]))
                    : state.closedColours,
                toteUnits: toteUnitsFromState(action.openTotes, state.toteUnits),
                toteWeights: toteWeightsFromState(action.openTotes, state.toteWeights),
                lastToteOutcome: { kind: "close", tote: action.result },
            };
        case "FINISHED":
            return {
                ...state,
                phase: "finished",
                backNavigationLocked: false,
                pending: null,
                lengthPrompt: false,
                bigFeedback: false,
                editScope: null,
                itemClosePrompt: null,
                reopenPrompt: null,
                reopenStartOver: false,
                swapColour: null,
                finishColour: null,
                finishedSummary: action.summary,
                toteUnits: {},
                toteWeights: {},
                lastToteOutcome: null,
                lastEditOutcome: null,
            };
        case "CLEAR_TOTE_OUTCOME":
            return { ...state, lastToteOutcome: null };
        default:
            return state;
    }
};

const BF_TOTE_STATUS_REASON_KEYS: Record<string, string> = {
    already_open_here: "bf_error_tote_already_open_here",
    already_open: "bf_error_tote_already_open",
    closed: "bf_error_tote_closed",
    awaiting_put_away: "bf_error_tote_awaiting_put_away",
    not_reopenable: "bf_error_tote_not_reopenable",
    unknown_tote: "bf_error_unknown_tote",
    colour_tote_limit: "bf_error_colour_tote_limit",
    colour_not_in_mode: "bf_error_colour_not_in_mode",
};

export const bfToteStatusReasonKey = (reason?: string): string | null =>
    BF_TOTE_STATUS_REASON_KEYS[reason ?? ""] ?? null;

// Who still holds an open fill-cycle on a tote (BE `heldBy` on an
// `already_open` refusal). A session idle for 30 minutes, or abandoned, has
// its totes booked and released by the BE sweep, so a non-active holder is
// a short-lived state.
export interface BfToteHolder {
    sessionId?: number | string;
    sessionStatus?: string;
    name?: string;
    locationBarcode?: string;
    colour?: string;
    openedAt?: string;
}

export interface BfErrorMessage {
    key: string;
    params?: Record<string, string | number>;
}

// The message for a tote-status refusal. An `already_open` that names its
// holder says who has the tote, as which colour, at which desk, and whether
// that session is still live, so the desk can go and get it closed instead
// of guessing. Anything else keeps its plain reason key.
export const bfToteStatusMessage = (
    data: { reason?: string; heldBy?: BfToteHolder | null } | undefined,
    colourLabel: (colour: string) => string,
): BfErrorMessage | null => {
    const key = bfToteStatusReasonKey(data?.reason);
    if (!key) return null;
    const holder = data?.heldBy;
    if (data?.reason !== "already_open" || !holder?.name) return { key };
    return {
        key: holder.sessionStatus === "active"
            ? "bf_error_tote_held_by"
            : "bf_error_tote_held_by_abandoned",
        params: {
            name: holder.name,
            location: holder.locationBarcode ?? "",
            colour: holder.colour ? colourLabel(holder.colour) : "",
        },
    };
};

export const bfApiReasonKey = (reason?: string): string => {
    const known: Record<string, string> = {
        ...BF_TOTE_STATUS_REASON_KEYS,
        unknown_location: "bf_error_unknown_location",
        not_bargainfox_location: "bf_error_not_location",
        active_session_elsewhere: "bf_error_active_elsewhere",
        colour_and_tote_required: "bf_error_tote_required",
        stored: "bf_error_tote_stored",
        colour_already_open: "bf_error_colour_already_open",
        old_tote_required: "bf_error_old_tote_required",
        no_active_session: "bf_error_no_session",
        no_session: "bf_error_no_session",
        session_ended: "bf_error_session_ended",
        colour_and_new_tote_required: "bf_error_colour_new_tote_required",
        no_event: "bf_error_no_event",
        missing_location_barcode: "bf_error_missing_location_barcode",
        no_open_tote_for_colour: "bf_error_no_colour_tote",
        already_booked: "bf_error_already_booked",
        all_totes_required: "bf_error_all_totes_required",
        totes_still_open: "bf_error_totes_still_open",
        wrong_tote: "bf_error_wrong_tote",
        tote_weight_limit: "bf_error_tote_weight_limit",
        event_not_pending: "bf_error_event_not_pending",
        event_not_found: "bf_error_event_not_found",
        not_big_mode: "bf_error_not_big_mode",
        event_and_answer_required: "bf_error_event_not_pending",
        dims_present: "bf_error_dims_present",
    };
    return known[reason ?? ""] ?? "bf_error_server";
};
